import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { inlineErrorClass } from '@/components/ui/inputStyles';

type DayRow = {
  ymd: string;
  uv: number;
  pv: number;
  launches: number;
  entry_views: number;
  dials: number;
  reward_ads: number;
  reward_ads_dial?: number;
  reward_ads_pin?: number;
  reward_ads_refresh?: number;
  share_pin_uv?: number;
  share_pin_pv?: number;
  share_pin_grants?: number;
  new_users: number;
  searches: number;
  posts?: number;
  posts_dawn?: number;
  posts_morning?: number;
  posts_afternoon?: number;
  posts_evening?: number;
};

type AnalyticsResp = {
  ok: boolean;
  miniprogram?: { id: number; name: string; public_id: string };
  server_time?: string;
  timezone?: string;
  tracking_since?: string;
  today?: DayRow;
  yesterday?: DayRow;
  summary?: Omit<DayRow, 'ymd'>;
  daily?: DayRow[];
  range?: { from: string; to: string; days: number };
};

type MetricKey = keyof Omit<DayRow, 'ymd'>;

type MetricDef = {
  key: MetricKey;
  label: string;
  short: string;
  hint: string;
  accent: string;
  icon: string;
  primary?: boolean;
};

const METRICS: MetricDef[] = [
  {
    key: 'uv',
    label: '独立访客 UV',
    short: 'UV',
    hint: '当日去重访客（登录用户或设备指纹）',
    accent: 'from-sky-500 to-blue-600',
    icon: 'fa-users',
    primary: true,
  },
  {
    key: 'pv',
    label: '页面访问 PV',
    short: 'PV',
    hint: 'bootstrap、详情页等 API 触发的访问次数',
    accent: 'from-violet-500 to-purple-600',
    icon: 'fa-eye',
    primary: true,
  },
  {
    key: 'launches',
    label: '打开次数',
    short: '打开',
    hint: '小程序 bootstrap 加载次数，近似启动量',
    accent: 'from-teal-500 to-emerald-600',
    icon: 'fa-mobile-screen',
    primary: true,
  },
  {
    key: 'entry_views',
    label: '详情浏览',
    short: '详情',
    hint: '信息详情页加载次数',
    accent: 'from-indigo-500 to-indigo-700',
    icon: 'fa-file-lines',
    primary: true,
  },
  {
    key: 'share_pin_uv',
    label: '转发引流 UV',
    short: '转UV',
    hint: '通过发布人转发卡片进入的独立打开者（当日 opener 去重）',
    accent: 'from-emerald-500 to-green-600',
    icon: 'fa-user-group',
    primary: true,
  },
  {
    key: 'share_pin_pv',
    label: '转发引流 PV',
    short: '转PV',
    hint: '通过发布人转发卡片进入详情的打开次数',
    accent: 'from-lime-500 to-emerald-600',
    icon: 'fa-share-nodes',
    primary: true,
  },
  {
    key: 'dials',
    label: '拨号次数',
    short: '拨号',
    hint: '用户点击拨号并上报的次数',
    accent: 'from-amber-500 to-orange-600',
    icon: 'fa-phone',
  },
  {
    key: 'reward_ads',
    label: '激励视频完播',
    short: '激励',
    hint: '完整观看并成功兑换：拨号次数、置顶或刷新（合计）',
    accent: 'from-rose-500 to-pink-600',
    icon: 'fa-clapperboard',
  },
  {
    key: 'new_users',
    label: '新增用户',
    short: '新用户',
    hint: 'C 端首次登录注册的小程序用户',
    accent: 'from-cyan-500 to-teal-600',
    icon: 'fa-user-plus',
  },
  {
    key: 'searches',
    label: '搜索次数',
    short: '搜索',
    hint: '首页有效关键词搜索',
    accent: 'from-slate-600 to-slate-800',
    icon: 'fa-magnifying-glass',
  },
  {
    key: 'posts',
    label: '信息发布数',
    short: '发布',
    hint: 'C 端用户提交发布成功次数（含待审核）',
    accent: 'from-lime-500 to-green-600',
    icon: 'fa-pen-to-square',
  },
];

const REWARD_AD_SUBMETRICS: { key: 'reward_ads_dial' | 'reward_ads_pin' | 'reward_ads_refresh'; label: string; hint: string }[] = [
  { key: 'reward_ads_dial', label: '拨号', hint: '兑换拨号次数' },
  { key: 'reward_ads_pin', label: '置顶', hint: '兑换信息置顶' },
  { key: 'reward_ads_refresh', label: '刷新', hint: '兑换信息刷新' },
];

const SHARE_PIN_SUBMETRICS: { key: 'share_pin_uv' | 'share_pin_pv' | 'share_pin_grants'; label: string; hint: string }[] = [
  { key: 'share_pin_uv', label: '引流 UV', hint: '转发进入的独立打开者' },
  { key: 'share_pin_pv', label: '引流 PV', hint: '转发进入的打开次数' },
  { key: 'share_pin_grants', label: '达标置顶', hint: '转发达标自动置顶次数' },
];

const POST_SLOT_METRICS: {
  key: 'posts_dawn' | 'posts_morning' | 'posts_afternoon' | 'posts_evening';
  label: string;
  range: string;
  accent: string;
}[] = [
  { key: 'posts_dawn', label: '凌晨', range: '00:00–06:00', accent: 'bg-indigo-400' },
  { key: 'posts_morning', label: '上午', range: '06:00–12:00', accent: 'bg-sky-400' },
  { key: 'posts_afternoon', label: '下午', range: '12:00–18:00', accent: 'bg-amber-400' },
  { key: 'posts_evening', label: '晚上', range: '18:00–24:00', accent: 'bg-violet-400' },
];

const CHART_BAR_AREA_PX = 160;

const PERIOD_OPTIONS = [
  { days: 7, label: '近 7 天' },
  { days: 14, label: '近 14 天' },
  { days: 30, label: '近 30 天' },
] as const;

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (n >= 100000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString('zh-CN');
}

function deltaPercent(today: number, yesterday: number): number | null {
  if (yesterday <= 0) {
    return today > 0 ? 100 : null;
  }
  return Math.round(((today - yesterday) / yesterday) * 1000) / 10;
}

function DeltaBadge({ today, yesterday }: { today: number; yesterday: number }) {
  const pct = deltaPercent(today, yesterday);
  if (pct === null) {
    return <span className="text-xs font-medium text-slate-400">较昨日 —</span>;
  }
  const up = pct >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        up ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
      )}
    >
      <i className={cn('fa-solid text-[10px]', up ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down')} aria-hidden />
      {up ? '+' : ''}
      {pct}%
      <span className="font-normal text-slate-500">较昨日</span>
    </span>
  );
}

function KpiCard({
  def,
  todayVal,
  yesterdayVal,
}: {
  def: MetricDef;
  todayVal: number;
  yesterdayVal: number;
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/[0.03]',
        def.primary && 'lg:p-5'
      )}
    >
      <div
        className={cn(
          'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-[0.12]',
          def.accent
        )}
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{def.label}</p>
          <p
            className={cn(
              'mt-1 font-bold tabular-nums tracking-tight text-slate-900',
              def.primary ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'
            )}
          >
            {formatNum(todayVal)}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{def.hint}</p>
        </div>
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-md',
            def.accent
          )}
        >
          <i className={cn('fa-solid', def.icon, def.primary ? 'text-lg' : 'text-base')} aria-hidden />
        </span>
      </div>
      <div className="relative mt-3 border-t border-slate-100 pt-3">
        <DeltaBadge today={todayVal} yesterday={yesterdayVal} />
        <span className="ml-2 text-xs text-slate-400">昨日 {formatNum(yesterdayVal)}</span>
      </div>
    </div>
  );
}

function TrendChart({ daily, metric }: { daily: DayRow[]; metric: MetricDef }) {
  const values = daily.map((d) => Math.max(0, Number(d[metric.key]) || 0));
  const max = Math.max(1, ...values);
  const last = daily[daily.length - 1];
  const prev = daily.length > 1 ? daily[daily.length - 2] : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{metric.label}趋势</p>
          <p className="mt-0.5 text-xs text-slate-500">每日 {metric.short} 柱状对比</p>
        </div>
        {last ? (
          <div className="text-right">
            <p className="text-2xl font-bold tabular-nums text-slate-900">{formatNum(Number(last[metric.key]) || 0)}</p>
            <p className="text-xs text-slate-500">
              {last.ymd.slice(5)}{' '}
              {prev ? <DeltaBadge today={Number(last[metric.key]) || 0} yesterday={Number(prev[metric.key]) || 0} /> : null}
            </p>
          </div>
        ) : null}
      </div>
      <div className="flex gap-1 sm:gap-1.5" style={{ height: CHART_BAR_AREA_PX }}>
        {daily.map((d) => {
          const v = Math.max(0, Number(d[metric.key]) || 0);
          const barPx = v > 0 ? Math.max(6, Math.round((v / max) * CHART_BAR_AREA_PX)) : 2;
          const isToday = d.ymd === last?.ymd;
          return (
            <div
              key={d.ymd}
              className="group flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
              title={`${d.ymd}：${formatNum(v)}`}
            >
              <div
                className={cn(
                  'w-full min-w-[6px] rounded-t-md bg-gradient-to-t transition-all',
                  metric.accent,
                  isToday ? 'opacity-100 ring-2 ring-offset-1 ring-slate-400/40' : 'opacity-80 hover:opacity-100'
                )}
                style={{ height: `${barPx}px` }}
              />
              <span
                className={cn(
                  'truncate text-[10px] tabular-nums leading-none',
                  isToday ? 'font-bold text-slate-800' : 'text-slate-400'
                )}
              >
                {d.ymd.slice(8)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PostSlotBreakdownCard({
  today,
  summary,
  days,
}: {
  today: DayRow;
  summary: Omit<DayRow, 'ymd'>;
  days: number;
}) {
  const slots = POST_SLOT_METRICS.map((s) => ({
    ...s,
    todayVal: Math.max(0, Number(today[s.key]) || 0),
    periodVal: Math.max(0, Number(summary[s.key]) || 0),
  }));
  const todayTotal = Math.max(0, Number(today.posts) || 0);
  const slotSum = slots.reduce((a, s) => a + s.todayVal, 0);
  const stackTotal = Math.max(todayTotal, slotSum, 1);

  return (
    <Card title="信息发布时段" description="按北京时间（Asia/Shanghai）统计用户提交发布">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-3xl font-bold tabular-nums text-slate-900">{formatNum(todayTotal)}</p>
          <p className="mt-1 text-xs text-slate-500">今日发布 · {days} 日合计 {formatNum(Number(summary.posts) || 0)}</p>
        </div>
        {todayTotal > 0 ? (
          <div className="flex h-3 min-w-[200px] flex-1 max-w-md overflow-hidden rounded-full bg-slate-100">
            {slots.map((s) =>
              s.todayVal > 0 ? (
                <div
                  key={s.key}
                  className={cn('h-full transition-all', s.accent)}
                  style={{ width: `${(s.todayVal / stackTotal) * 100}%` }}
                  title={`${s.label} ${formatNum(s.todayVal)}`}
                />
              ) : null
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400">今日暂无发布</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {slots.map((s) => (
          <div key={s.key} className="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', s.accent)} aria-hidden />
              <p className="text-xs font-semibold text-slate-700">{s.label}</p>
            </div>
            <p className="mt-2 text-xl font-bold tabular-nums text-slate-900">{formatNum(s.todayVal)}</p>
            <p className="mt-1 text-[11px] text-slate-500">{s.range}</p>
            <p className="mt-1 text-[10px] text-slate-400">{days} 日 {formatNum(s.periodVal)}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function MiniprogramAnalyticsPage() {
  const { id } = useParams();
  const mpId = Number(id) || 0;
  const [days, setDays] = useState<number>(14);
  const [data, setData] = useState<AnalyticsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(
    async (silent = false) => {
      if (mpId < 1) return;
      if (silent) setRefreshing(true);
      else setLoading(true);
      setErr('');
      try {
        const r = await apiFetch<AnalyticsResp>('get_miniprogram_analytics', {
          method: 'GET',
          query: { miniprogram_id: mpId, days },
        });
        if (r.ok) {
          setData(r);
        } else {
          setErr('加载统计失败');
        }
      } catch {
        setErr('加载统计失败');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [mpId, days]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => void load(true), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const today = data?.today;
  const yesterday = data?.yesterday;
  const daily = data?.daily ?? [];
  const summary = data?.summary;
  const mp = data?.miniprogram;

  const primaryMetrics = useMemo(() => METRICS.filter((m) => m.primary), []);
  const secondaryMetrics = useMemo(() => METRICS.filter((m) => !m.primary), []);

  if (mpId < 1) {
    return (
      <div className="space-y-4">
        <p className={inlineErrorClass}>无效的小程序 ID</p>
        <Link to="/miniprograms" className="text-teal-700 underline">
          返回小程序管理
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="访问统计"
        description={
          mp
            ? `「${mp.name}」小程序运营数据 · 服务端实时埋点，无需改小程序代码`
            : '小程序运营数据 · 服务端实时埋点'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/miniprograms">
              <Button type="button" variant="secondary">
                返回列表
              </Button>
            </Link>
            <Link to={`/miniprograms/${mpId}?view=config`}>
              <Button type="button" variant="ghost">
                参数配置
              </Button>
            </Link>
            <Button type="button" variant="primary" disabled={refreshing} onClick={() => void load(true)}>
              {refreshing ? '刷新中…' : '刷新数据'}
            </Button>
          </div>
        }
      />

      {mp ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <span>
            小程序：<strong className="text-slate-900">{mp.name}</strong>
          </span>
          <span className="font-mono text-xs text-slate-500">{mp.public_id}</span>
          {data?.server_time ? <span>统计时间 {data.server_time}（{data.timezone || 'Asia/Shanghai'}）</span> : null}
          {data?.tracking_since ? (
            <span className="text-xs text-slate-500">统计自 {data.tracking_since} 起（部署后累计）</span>
          ) : (
            <span className="text-xs text-amber-800">尚无历史数据，用户访问小程序后将自动记录</span>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => setDays(opt.days)}
            className={cn(
              'rounded-xl px-4 py-2 text-sm font-semibold transition',
              days === opt.days
                ? 'bg-slate-900 text-white shadow-md'
                : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {err ? <p className={inlineErrorClass}>{err}</p> : null}

      {loading ? (
        <div className="flex min-h-[320px] items-center justify-center">
          <Spinner label="加载统计数据" />
        </div>
      ) : (
        <>
          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">今日核心指标</h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {primaryMetrics.map((def) => (
                <KpiCard
                  key={def.key}
                  def={def}
                  todayVal={Number(today?.[def.key]) || 0}
                  yesterdayVal={Number(yesterday?.[def.key]) || 0}
                />
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">互动与转化</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {secondaryMetrics.map((def) => (
                <KpiCard
                  key={def.key}
                  def={def}
                  todayVal={Number(today?.[def.key]) || 0}
                  yesterdayVal={Number(yesterday?.[def.key]) || 0}
                />
              ))}
            </div>
          </section>

          {today && summary ? (
            <PostSlotBreakdownCard today={today} summary={summary} days={days} />
          ) : null}

          {today && summary ? (
            <Card
              title="转发置顶引流"
              description="发布人转发卡片带来的打开：UV 按 opener 日去重，PV 为打开次数，达标置顶为转发达标自动置顶次数"
            >
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 px-3 py-2.5">
                  <p className="text-[11px] font-medium text-slate-500">今日引流</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                    UV {formatNum(Number(today.share_pin_uv) || 0)} · PV {formatNum(Number(today.share_pin_pv) || 0)}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">
                    {days} 日 UV {formatNum(Number(summary.share_pin_uv) || 0)} · PV{' '}
                    {formatNum(Number(summary.share_pin_pv) || 0)}
                  </p>
                </div>
                {SHARE_PIN_SUBMETRICS.map((sub) => (
                  <div key={sub.key} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                    <p className="text-[11px] font-medium text-slate-500" title={sub.hint}>
                      今日·{sub.label}
                    </p>
                    <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                      {formatNum(Number(today[sub.key]) || 0)}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {days} 日 {formatNum(Number(summary[sub.key]) || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {today && summary ? (
            <Card title="激励视频明细" description="按业务场景拆分，便于核对置顶/刷新是否计入">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-rose-100 bg-rose-50/40 px-3 py-2.5">
                  <p className="text-[11px] font-medium text-slate-500">今日合计</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                    {formatNum(Number(today.reward_ads) || 0)}
                  </p>
                </div>
                {REWARD_AD_SUBMETRICS.map((sub) => (
                  <div key={sub.key} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                    <p className="text-[11px] font-medium text-slate-500" title={sub.hint}>
                      今日·{sub.label}
                    </p>
                    <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                      {formatNum(Number(today[sub.key]) || 0)}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {days} 日 {formatNum(Number(summary[sub.key]) || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {summary ? (
            <Card title={`${days} 日汇总`} className="border-teal-100 bg-gradient-to-br from-white to-teal-50/30">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
                {METRICS.map((def) => (
                  <div key={def.key} className="rounded-xl border border-white/80 bg-white/90 px-3 py-2.5 shadow-sm">
                    <p className="text-[11px] font-medium text-slate-500">{def.short}</p>
                    <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
                      {formatNum(Number(summary[def.key]) || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <TrendChart daily={daily} metric={METRICS[0]} />
            <TrendChart daily={daily} metric={METRICS[1]} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TrendChart daily={daily} metric={METRICS[4]} />
            <TrendChart daily={daily} metric={METRICS[5]} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TrendChart daily={daily} metric={METRICS[8]} />
          </div>

          <Card title="每日明细" description="真实服务端埋点数据，便于核对运营波动">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2.5">日期</th>
                    {METRICS.map((m) => (
                      <th key={m.key} className="px-3 py-2.5 text-right">
                        {m.short}
                      </th>
                    ))}
                    {REWARD_AD_SUBMETRICS.map((sub) => (
                      <th key={sub.key} className="px-3 py-2.5 text-right text-rose-600/90">
                        激励·{sub.label}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-right text-emerald-700/90">转发·达标置顶</th>
                    {POST_SLOT_METRICS.map((sub) => (
                      <th key={sub.key} className="px-3 py-2.5 text-right text-green-700/90">
                        发布·{sub.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...daily].reverse().map((row) => {
                    const isToday = row.ymd === today?.ymd;
                    return (
                      <tr key={row.ymd} className={cn(isToday && 'bg-teal-50/60 font-medium')}>
                        <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-slate-800">
                          {row.ymd}
                          {isToday ? (
                            <span className="ml-2 rounded bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                              今日
                            </span>
                          ) : null}
                        </td>
                        {METRICS.map((m) => (
                          <td key={m.key} className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                            {formatNum(Number(row[m.key]) || 0)}
                          </td>
                        ))}
                        {REWARD_AD_SUBMETRICS.map((sub) => (
                          <td key={sub.key} className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                            {formatNum(Number(row[sub.key]) || 0)}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                          {formatNum(Number(row.share_pin_grants) || 0)}
                        </td>
                        {POST_SLOT_METRICS.map((sub) => (
                          <td key={sub.key} className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                            {formatNum(Number(row[sub.key]) || 0)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="text-xs leading-relaxed text-slate-500">
            说明：UV 按登录 openid 或 IP+UA 指纹去重；PV 含 bootstrap 与详情页访问；拨号来自
            minipro_record_dial。激励视频完播含拨号、置顶、刷新三类兑换。信息发布数来自 mp_entry_create
            成功提交，时段按北京时间划分。历史发布会从 wes_entries 自动回填。页面每 60 秒自动刷新今日数据。
          </p>
        </>
      )}
    </div>
  );
}
